import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { CLAVE_TEMA, TEMAS, TEMA_POR_DEFECTO, claveDeTema } from './themes';

interface TemaContexto {
  tema: string;
  elegirTema: (id: string) => void;
  /** Mientras sea `true`, la pantalla se ve con el tema por defecto. */
  ignorarTema: (ignorar: boolean) => void;
}

const Contexto = createContext<TemaContexto>({
  tema: TEMA_POR_DEFECTO,
  elegirTema: () => undefined,
  ignorarTema: () => undefined,
});

const esConocido = (id: string | null): id is string =>
  id !== null && TEMAS.some((tema) => tema.id === id);

/** Quién tiene la sesión abierta, si la hay. */
const usuarioActual = (): string | null => {
  try {
    return sessionStorage.getItem('username');
  } catch {
    return null;
  }
};

/**
 * El tema de esta persona; si aún no eligió ninguno, el último elegido en el
 * equipo, y si tampoco lo hay, el de siempre.
 */
const leerGuardado = (): string => {
  try {
    const propio = localStorage.getItem(claveDeTema(usuarioActual()));
    if (esConocido(propio)) {
      return propio;
    }
    const delEquipo = localStorage.getItem(CLAVE_TEMA);
    return esConocido(delEquipo) ? delEquipo : TEMA_POR_DEFECTO;
  } catch {
    // Navegación privada o almacenamiento bloqueado: no es motivo para fallar.
    return TEMA_POR_DEFECTO;
  }
};

/**
 * Deja el tema elegido en un atributo de la raíz del documento; el CSS hace el
 * resto. Envuelve a toda la aplicación; las pantallas que no deben cambiar con
 * el tema, como el login, lo piden con `useSinTema`.
 */
export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const [tema, setTema] = useState(leerGuardado);
  const [ignorado, setIgnorado] = useState(false);

  // Antes de pintar, para que el login no parpadee con el tema elegido.
  useLayoutEffect(() => {
    document.documentElement.dataset.theme = ignorado ? TEMA_POR_DEFECTO : tema;
  }, [tema, ignorado]);

  useEffect(() => {
    try {
      // En las dos: la propia manda, y la general sirve para la pantalla de
      // acceso, donde todavía no se sabe quién entra.
      localStorage.setItem(claveDeTema(usuarioActual()), tema);
      localStorage.setItem(CLAVE_TEMA, tema);
    } catch {
      // Sin dónde guardarlo, el tema dura lo que la pestaña. Nada más.
    }
  }, [tema]);

  const elegirTema = useCallback((id: string) => {
    if (esConocido(id)) {
      setTema(id);
    }
  }, []);

  // Mismo objeto mientras no cambie el tema: así no se repinta todo lo que lo lee.
  const valor = useMemo(
    () => ({ tema, elegirTema, ignorarTema: setIgnorado }),
    [tema, elegirTema]
  );

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
};

export const useTema = () => useContext(Contexto);

/** La pantalla que lo llama se ve siempre con el tema por defecto. */
export const useSinTema = () => {
  const { ignorarTema } = useContext(Contexto);
  useLayoutEffect(() => {
    ignorarTema(true);
    return () => ignorarTema(false);
  }, [ignorarTema]);
};

export default ThemeProvider;
